/* ============================================================
   theme.js — light/dark for FOX

   One source of truth for which theme is on. The theme itself lives in CSS:
   index.css declares the tokens under :root and overrides them under
   :root[data-theme="dark"], so everything this module does is decide which
   of those two blocks applies and tell the parts of the app that cannot read
   CSS -- the ECharts options -- that it changed.

   FOX is a light application by default. Dark is opt-in: nothing turns it on
   but the toggle, and once turned on it stays on for that browser until the
   toggle turns it off again. The OS setting is deliberately not consulted --
   a person on a dark-themed laptop still gets light FOX until they ask for
   otherwise.

   The attribute is set on <html> before React mounts, by the inline script in
   public/index.html. That script is the one that prevents a white flash for
   someone who HAS chosen dark, and it duplicates readStored() below on
   purpose: nothing bundled can run early enough to do it. The two must agree,
   so if the storage key or the attribute values change here, change them
   there too.
   ============================================================ */

import { useEffect, useState } from "react";

const STORAGE_KEY = "fox_theme";

/* The browser chrome around an installed PWA. index.html ships the light
   value; it is swapped rather than left alone because iOS paints the status
   bar with it, and a #f5f6f8 bar above a #0d1117 page is exactly the mismatch
   the comment on that meta tag was written about. */
const THEME_COLOR = { light: "#f5f6f8", dark: "#0d1117" };

const listeners = new Set();

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === "dark" || raw === "light" ? raw : null;
  } catch {
    // localStorage unavailable — the choice just won't persist across visits
    return null;
  }
}

/** "dark" | "light" — what is on the document right now. */
export function getTheme() {
  const attr = document.documentElement.getAttribute("data-theme");
  return attr === "dark" ? "dark" : "light";
}

export function isDark() {
  return getTheme() === "dark";
}

/**
 * Apply a theme and remember it.
 *
 * @param {"dark"|"light"} theme
 * @param {boolean} persist - false for a preview that should not be recorded
 */
export function setTheme(theme, persist = true) {
  const next = theme === "dark" ? "dark" : "light";
  if (getTheme() === next) return next;

  document.documentElement.setAttribute("data-theme", next);

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLOR[next]);

  if (persist) {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage unavailable — the choice just won't persist across visits
    }
  }

  listeners.forEach((fn) => fn(next));
  return next;
}

export function toggleTheme() {
  return setTheme(isDark() ? "light" : "dark");
}

/**
 * Called on every theme change. Returns its own unsubscribe.
 *
 * Charts are the reason this exists: an ECharts `option` is a plain object
 * built once from resolved colour values, so unlike a DOM node it cannot
 * re-read a CSS variable when the variable changes. Anything holding such an
 * object has to be told to rebuild it.
 */
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Bring the document in line with the stored choice, defaulting to light.
 * Called once from index.js.
 *
 * Light is the default for everyone who has not picked, every time -- there is
 * no OS fallback and nothing else that can turn dark on. A stored "dark" is the
 * only thing that does, and it survives until the toggle clears it.
 *
 * `persist: false` so that arriving on the default does not write it down. The
 * absence of a stored value is what "has not chosen" means, and recording
 * "light" here would make an untouched browser indistinguishable from one where
 * someone turned dark on and back off. Both get light either way, so nothing
 * visible depends on it today -- but it keeps the stored value meaning a
 * decision.
 *
 * The inline script in index.html has normally set the attribute already; this
 * runs anyway so the listeners and the theme-color meta are correct even if
 * that script was stripped or failed.
 */
export function initTheme() {
  setTheme(readStored() || "light", false);
}

/**
 * true while dark mode is on, re-rendering the caller when that changes.
 *
 * Use this anywhere a colour has to be picked in JavaScript rather than by
 * CSS -- chart options, canvas drawing, an inline style that cannot reach a
 * token. Plain CSS needs none of it: those rules already re-resolve their
 * variables when the attribute on <html> flips.
 */
export function useIsDark() {
  const [dark, setDark] = useState(isDark);
  useEffect(() => subscribe((theme) => setDark(theme === "dark")), []);
  return dark;
}

/* Cache keyed on the theme: getComputedStyle forces a style recalculation, and
   a chart option can ask for a dozen tokens while ECharts is mid-render. The
   whole cache is dropped on any theme change, below. */
let varCache = new Map();
subscribe(() => {
  varCache = new Map();
});

/**
 * The computed value of a CSS custom property, as a real colour string.
 *
 * This is the bridge for anything that cannot use a CSS variable directly.
 * ECharts draws to a canvas, so `var(--text2)` in a chart option is not a
 * colour it can paint -- it needs the resolved "#8b949e". React inline styles
 * need none of this and should keep using var(), because the browser
 * re-resolves those by itself when the theme flips.
 *
 * The value is read from the live document, so it follows whichever theme is
 * on. A component calling it still has to re-render for a new value to reach
 * the chart: take useIsDark() and key the option's useMemo on it.
 *
 * @param {string} name     token name, including the leading dashes
 * @param {string} fallback returned when the token is unset or unreadable,
 *                          which is the case under jsdom in tests
 */
export function cssVar(name, fallback = "") {
  if (varCache.has(name)) return varCache.get(name);
  let value = fallback;
  try {
    const read = getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim();
    if (read) value = read;
  } catch {
    // No computed style available (jsdom, detached document) — use the fallback
  }
  varCache.set(name, value);
  return value;
}

/**
 * A palette object whose values are read from CSS tokens on every access.
 *
 * Dashboards keep a handful of module-scope colour constants -- ink, gridlines,
 * status hues -- duplicated out of index.css because ECharts takes colours as
 * values rather than variables. A plain `const` freezes whichever theme was on
 * when the module was imported, which for a lazily-loaded page is whichever
 * theme the user happened to be in when they first opened it.
 *
 * Declaring them here instead keeps the shape and the call sites, and re-reads
 * the token each time one is touched:
 *
 *   const INK = themedColors({ text: ["--text", "#1a1d23"] });
 *   ...
 *   axisLabel: { color: INK.text }
 *
 * The component still has to re-render for a rebuilt option to reach the chart;
 * that is what useIsDark() is for. This only guarantees that when it does
 * rebuild, the colours are the current theme's.
 *
 * @param {Object<string, [string, string]>} spec  name -> [token, fallback]
 */
export function themedColors(spec) {
  const out = {};
  Object.entries(spec).forEach(([key, [token, fallback]]) => {
    Object.defineProperty(out, key, {
      get: () => cssVar(token, fallback),
      enumerable: true,
    });
  });
  return out;
}
